/**
 * VersionHistoryPanel — chapter version history sidebar panel.
 *
 * Shows list of versions (metadata), allows preview and restore.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { motion, AnimatePresence } from 'motion/react';
import {
  History,
  X,
  RotateCcw,
  Eye,
  ArrowLeft,
  Plus,
  Clock,
  Cpu,
  Save,
  User,
  Lock,
} from 'lucide-react';
import { api, AddonRequiredError, type ChapterVersionMeta, type ChapterVersionFull, type ChapterVersionLabel } from '../lib/api';
import { notify } from '../lib/toast';
import { cn } from '../lib/utils';

/** Show the add-on upsell toast for a blocked action, or the generic error otherwise. */
function reportError(err: unknown, fallbackMessage: string): void {
  if (err instanceof AddonRequiredError) {
    notify.addonRequired(err.addonId, err.message);
  } else {
    notify.error(fallbackMessage);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(dateStr: string, t: TFunction): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return t('versionHistory.timeNow', 'agora');
  if (diffMin < 60) return t('versionHistory.timeMinAgo', { count: diffMin, defaultValue: 'há {{count}} min' });
  if (diffHour < 24) return t('versionHistory.timeHourAgo', { count: diffHour, defaultValue: 'há {{count}}h' });
  if (diffDay === 1) return t('versionHistory.timeYesterday', 'ontem');
  if (diffDay < 7) return t('versionHistory.timeDaysAgo', { count: diffDay, defaultValue: 'há {{count}} dias' });

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: diffDay > 365 ? 'numeric' : undefined,
  });
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const LABEL_ICON_COLOR: Record<ChapterVersionLabel, { Icon: React.ElementType; color: string }> = {
  manual: {
    Icon: Save,
    color: 'text-blue-500',
  },
  autosave: {
    Icon: Clock,
    color: 'text-muted-foreground',
  },
  'pre-ai': {
    Icon: Cpu,
    color: 'text-violet-500',
  },
  restore: {
    Icon: RotateCcw,
    color: 'text-amber-500',
  },
};

function getLabelText(t: TFunction, label: string): string {
  switch (label as ChapterVersionLabel) {
    case 'manual':
      return t('versionHistory.labelManual', 'Manual');
    case 'autosave':
      return t('versionHistory.labelAutosave', 'Autosave');
    case 'pre-ai':
      return t('versionHistory.labelPreAi', 'Pré-IA');
    case 'restore':
      return t('versionHistory.labelRestore', 'Restauração');
    default:
      return t('versionHistory.labelManual', 'Manual');
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface VersionHistoryPanelProps {
  chapterId: string;
  onClose: () => void;
  /** Called after a successful restore so the editor can reload content */
  onRestored: (newContent: string) => void;
  /** When true, renders inner content only (no wrapper/animation) for use inside a vaul Drawer. */
  embedded?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VersionHistoryPanel({ chapterId, onClose, onRestored, embedded = false }: VersionHistoryPanelProps) {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<ChapterVersionMeta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<ChapterVersionFull | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [confirmRestoreId, setConfirmRestoreId] = useState<string | null>(null);
  // Set when the server blocks version history behind the Craft add-on
  // (HTTP 402 addon_required) — replaces the list with an upsell card.
  const [addonBlocked, setAddonBlocked] = useState<{ addonId: string; message: string } | null>(null);

  // Load versions list
  const loadVersions = useCallback(async () => {
    try {
      setIsLoading(true);
      setAddonBlocked(null);
      const res = await api.getChapterVersions(chapterId);
      setVersions(res.versions);
    } catch (err) {
      if (err instanceof AddonRequiredError) {
        setAddonBlocked({ addonId: err.addonId, message: err.message });
      } else {
        notify.error(t('versionHistory.loadVersionsError', 'Erro ao carregar histórico de versões'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [chapterId, t]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // Save version now
  const handleSaveNow = useCallback(async () => {
    try {
      setIsSaving(true);
      await api.createChapterVersion(chapterId);
      notify.success(t('versionHistory.saveVersionSuccess', 'Versão salva com sucesso'));
      await loadVersions();
    } catch (err) {
      reportError(err, t('versionHistory.saveVersionError', 'Erro ao salvar versão'));
    } finally {
      setIsSaving(false);
    }
  }, [chapterId, loadVersions, t]);

  // Preview version
  const handlePreview = useCallback(async (versionId: string) => {
    try {
      setIsLoadingPreview(true);
      const res = await api.getChapterVersion(chapterId, versionId);
      setPreviewVersion(res.version);
    } catch (err) {
      reportError(err, t('versionHistory.loadVersionError', 'Erro ao carregar versão'));
    } finally {
      setIsLoadingPreview(false);
    }
  }, [chapterId, t]);

  // Restore version
  const handleRestore = useCallback(async (versionId: string) => {
    try {
      setIsRestoring(true);
      const res = await api.restoreChapterVersion(chapterId, versionId);
      // cerimônia: a ampulheta vira
      window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'timeback' } }));
      setPreviewVersion(null);
      setConfirmRestoreId(null);
      onRestored(res.chapter.content ?? '');
      await loadVersions();
    } catch (err) {
      reportError(err, t('versionHistory.restoreError', 'Erro ao restaurar versão'));
    } finally {
      setIsRestoring(false);
    }
  }, [chapterId, onRestored, loadVersions, t]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Shared inner panel content — rendered inside the vaul Drawer (embedded) OR the
  // desktop side-panel motion.div. The Drawer provides overlay, drag-handle and
  // animation; the desktop branch handles its own slide-in animation.
  const inner = (<>
    {/* Header */}
      <div className="h-11 flex items-center px-4 gap-2 border-b border-border flex-shrink-0">
        <History size={16} className="text-muted-foreground flex-shrink-0" />
        <span className="text-sm font-medium flex-1">{t('versionHistory.title', 'Histórico de versões')}</span>
        {!addonBlocked && (
          <button
            onClick={handleSaveNow}
            disabled={isSaving}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
            title={t('versionHistory.saveVersionNow', 'Salvar versão agora')}
          >
            <Plus size={12} />
            {isSaving ? t('versionHistory.saving', 'Salvando…') : t('versionHistory.saveVersion', 'Salvar versão')}
          </button>
        )}
        <button
          onClick={onClose}
          className="p-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t('versionHistory.closeHistory', 'Fechar histórico')}
        >
          <X size={16} />
        </button>
      </div>

      {/* Preview mode */}
      <AnimatePresence mode="wait">
        {previewVersion ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col flex-1 min-h-0 overflow-hidden"
          >
            {/* Preview header */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/30 flex-shrink-0">
              <button
                onClick={() => setPreviewVersion(null)}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t('versionHistory.backToList', 'Voltar para lista')}
              >
                <ArrowLeft size={16} />
              </button>
              <div className="flex-1 min-w-0">
                {(() => {
                  const cfg = LABEL_ICON_COLOR[previewVersion.label as ChapterVersionLabel] ?? LABEL_ICON_COLOR.manual;
                  return (
                    <div className="flex items-center gap-1.5">
                      <cfg.Icon size={13} className={cfg.color} />
                      <span className="text-xs font-medium">{getLabelText(t, previewVersion.label)}</span>
                      <span className="text-xs text-muted-foreground">
                        · {formatDateTime(previewVersion.createdAt)}
                      </span>
                    </div>
                  );
                })()}
                <div className="text-xs text-muted-foreground mt-0.5">
                  {previewVersion.wordCount.toLocaleString('pt-BR')} {t('versionHistory.words', 'palavras')}
                  {previewVersion.creatorName && ` · ${previewVersion.creatorName}`}
                </div>
              </div>
            </div>

            {/* Preview content — flex-1 min-h-0 so it scrolls within the drawer cap */}
            <div
              className="flex-1 min-h-0 overflow-y-auto p-4 text-sm text-muted-foreground font-serif leading-relaxed"
              // Render HTML preview read-only
              dangerouslySetInnerHTML={{ __html: previewVersion.content }}
            />

            {/* Preview actions */}
            <div className="p-3 border-t border-border flex gap-2 flex-shrink-0">
              {confirmRestoreId === previewVersion.id ? (
                <>
                  <span className="flex-1 text-xs text-muted-foreground flex items-center">
                    {t('versionHistory.confirmRestoreQuestion', 'Confirmar restauração?')}
                  </span>
                  <button
                    onClick={() => handleRestore(previewVersion.id)}
                    disabled={isRestoring}
                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {isRestoring ? t('versionHistory.restoring', 'Restaurando…') : t('versionHistory.confirm', 'Confirmar')}
                  </button>
                  <button
                    onClick={() => setConfirmRestoreId(null)}
                    disabled={isRestoring}
                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-muted text-foreground hover:bg-muted/80 transition-colors"
                  >
                    {t('versionHistory.cancel', 'Cancelar')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setPreviewVersion(null)}
                    className="flex-1 py-1.5 rounded-md text-xs font-medium bg-muted text-foreground hover:bg-muted/80 transition-colors"
                  >
                    {t('versionHistory.close', 'Fechar')}
                  </button>
                  <button
                    onClick={() => setConfirmRestoreId(previewVersion.id)}
                    className="flex-1 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center justify-center gap-1"
                  >
                    <RotateCcw size={12} />
                    {t('versionHistory.restore', 'Restaurar')}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col flex-1 min-h-0 overflow-hidden"
          >
            {isLoading ? (
              <div className={cn('flex items-center justify-center', embedded ? 'py-8' : 'flex-1')}>
                <div className="text-xs text-muted-foreground">{t('versionHistory.loading', 'Carregando…')}</div>
              </div>
            ) : addonBlocked ? (
              <div className={cn('flex flex-col items-center justify-center gap-3 p-6 text-center', embedded ? '' : 'flex-1')}>
                <Lock size={32} className="text-muted-foreground/30" />
                <p className="text-sm font-medium text-foreground">
                  {t('versionHistory.addonRequiredTitle', 'Recurso do add-on Craft')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('versionHistory.addonRequiredDescription', 'Ative o add-on Craft para navegar e restaurar o histórico de versões deste capítulo.')}
                </p>
                <button
                  onClick={() => (window.location.href = '/app/account')}
                  className="mt-1 px-4 py-2 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  {t('versionHistory.viewAddons', 'Ver add-ons')}
                </button>
              </div>
            ) : versions.length === 0 ? (
              <div className={cn('flex flex-col items-center justify-center gap-3 p-6 text-center', embedded ? '' : 'flex-1')}>
                <History size={32} className="text-muted-foreground/30" />
                <p className="text-sm font-medium text-foreground">{t('versionHistory.noVersionsYet', 'Nenhuma versão ainda')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('versionHistory.noVersionsDescription', 'Salve uma versão manualmente ou escreva mais — versões automáticas são criadas a cada 10 min.')}
                </p>
                <button
                  onClick={handleSaveNow}
                  disabled={isSaving}
                  className="mt-1 px-4 py-2 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {isSaving ? t('versionHistory.saving', 'Salvando…') : t('versionHistory.saveVersionNow', 'Salvar versão agora')}
                </button>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-border">
                {versions.map((version) => {
                  const cfg = LABEL_ICON_COLOR[version.label as ChapterVersionLabel] ?? LABEL_ICON_COLOR.manual;
                  return (
                    <div
                      key={version.id}
                      className="group flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                    >
                      <div className={cn('mt-0.5 flex-shrink-0', cfg.color)}>
                        <cfg.Icon size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-xs font-medium text-foreground">{getLabelText(t, version.label)}</span>
                          <span className="text-xs text-muted-foreground">
                            · {version.wordCount.toLocaleString('pt-BR')} {t('versionHistory.wordsAbbrev', 'pal.')}
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <span>{formatRelativeTime(version.createdAt, t)}</span>
                          {version.creatorName && (
                            <>
                              <span>·</span>
                              <User size={10} />
                              <span className="truncate max-w-[100px]">{version.creatorName}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handlePreview(version.id)}
                        disabled={isLoadingPreview}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                        title={t('versionHistory.previewVersion', 'Visualizar versão')}
                        aria-label={t('versionHistory.previewVersion', 'Visualizar versão')}
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
  </>);

  if (embedded) {
    // Mobile: vaul DrawerContent provides the overlay, drag-handle and animation.
    // flex-1 min-h-0 lets the inner layout participate in the cap set on DrawerContent
    // (max-h-[90dvh]). The scroll regions (flex-1 min-h-0 overflow-y-auto) scroll
    // within whatever space remains after the header is laid out.
    return (
      <div className="flex flex-col flex-1 min-h-0 bg-card">
        {inner}
      </div>
    );
  }

  // Desktop: slide-in side panel from the right edge.
  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
      className="relative h-full w-[340px] flex-shrink-0 border-l flex flex-col overflow-hidden border-border bg-card"
    >
      {inner}
    </motion.div>
  );
}
